/**
 * lazyChunk — `React.lazy` with a deadline, and the ONLY way this app lazy-loads.
 *
 * WHY: a chunk request that HANGS (never resolves, never rejects) leaves the
 * Suspense boundary on its fallback forever. That is the production
 * "boot-stall: suspense" white screen the boot watchdog recorded on /profile —
 * two full minutes on a loader, no error thrown, so no crash log, no
 * ErrorBoundary, and no chunk recovery. `React.lazy` has no timeout of its own,
 * and neither has the browser's native dynamic `import()` that Vite compiles a
 * lazy import to — webpack at least gave up after 120 s, native import never
 * does. So EVERY `React.lazy` in the app goes through here (a test pins it):
 * an unwrapped one would hang silently and would not even show up in the
 * boot-stall snapshot's `pending` list, because a leaf chunk gets no
 * `<link rel=modulepreload>` the DOM could be asked about.
 *
 * The service worker deadlines its own `/static` fetches, which covers the
 * common case. This is the second layer, and it is not redundant: the
 * stale-chunk recovery in index.html UNREGISTERS every service worker before
 * reloading, so a client that just went through a recovery has no SW at all —
 * its chunk requests go straight to the network with nothing bounding them.
 *
 * HOW: race the import against a deadline and, on expiry, reject with an error
 * shaped like webpack's old chunk failure ("Loading chunk … failed"). That
 * wording is what the recovery chain — `ErrorBoundary`, `crashLogger`, the
 * inline boot listener — still keys on (src/utils/staleChunkPattern.js), so
 * the rejection lands in the existing, well-tested recovery path (branded
 * loader → cache-bust reload, loop-capped) instead of a dead-end white screen.
 * Turning a stall into an error is the whole point.
 *
 * A REJECTION is re-shaped the same way, but only when it IS a chunk error: the
 * browsers' native messages ("Failed to fetch dynamically imported module",
 * "Importing a module script failed.", the Safari MIME error …) become
 * `Loading chunk <name> failed. (<original message>)`, so the crash-log row
 * says WHICH chunk died and the original text (and any URL in it) survives for
 * the recovery's targeted cache heal. Any other rejection — a module that
 * loaded fine and threw while evaluating, a `TypeError` — passes through
 * untouched: that is a code bug, not a stale chunk, must NOT trigger a
 * recovery reload, and is logged as the crash it is.
 *
 * NOT A RETRY-IN-PLACE: calling `import()` again would NOT re-issue the
 * request — the module loader memoizes the in-flight promise, so a second call
 * returns the SAME hung promise. Escaping a wedged chunk load genuinely
 * requires a fresh document, which is what the recovery reload provides.
 */
import React from "react";
import { isStaleChunkMessage } from "./staleChunkPattern";

// Bounded on BOTH sides, and both bounds live in other files:
//   • ABOVE the service worker's `/static` worst case — FETCH_DEADLINE_MS +
//     RETRY_DEADLINE_MS in public/sw.js (13000 today). Below that sum, this
//     deadline wins the race on every wedged connection: it aborts the SW's
//     poison-retry a moment before it would have answered, and rejects with a
//     message carrying NO chunk URL, so the recovery's `healChunk` — the whole
//     point of that retry — is skipped. Production ran 15000 against a 16000
//     sum and did exactly this.
//   • BELOW the 20s boot-stall watchdog (BOOT_STALL_MS in utils/crashLogger.js)
//     so a hung chunk turns into a recovery rather than a white-screen report.
//
// Note the asymmetry with the SW's deadline: `fetch()` settles on response
// HEADERS, so that one bounds only connect/TTFB and never truncates a slow
// download. This one races the WHOLE import — transfer and execute included —
// so it IS a full-transfer budget, and a heavy chunk on a weak link spends it.
// src/__tests__/chunkDeadlines.test.js pins the ordering across the three files.
const CHUNK_DEADLINE_MS = 15000;

// Test seam — mirrors the `__*ForTests` helpers in utils/serverTime.js.
let chunkDeadlineMs = CHUNK_DEADLINE_MS;
export function __setChunkDeadlineForTests(ms) {
  chunkDeadlineMs = typeof ms === "number" ? ms : CHUNK_DEADLINE_MS;
}

// Chunk loads currently in flight, by name. The boot-stall snapshot in
// crashLogger reads this: a hung leaf chunk leaves no `<script>` and no
// `<link>` in the DOM, so nothing else on the page can name it. A name is in
// the set from the moment `factory()` is called until the import settles
// either way — which means the snapshot (taken 20 s after load) can only ever
// see a chunk whose request started more than BOOT_STALL_MS − CHUNK_DEADLINE_MS
// after load; anything earlier has already been deadlined out of the set and
// into a recovery.
const inFlight = new Set();

/** Names of the lazy chunks whose import has not settled yet (insertion order). */
export function getInFlightChunkNames() {
  return Array.from(inFlight);
}

/**
 * Wrap a dynamic-import factory so a hung load fails loudly instead of hanging.
 * @param {() => Promise<any>} factory e.g. `() => import("./components/Foo")`
 * @param {string} [name] label for the error message (aids crash-log triage)
 */
export function lazyChunk(factory, name) {
  const label = name || "lazy";
  return React.lazy(() => {
    let timer;
    inFlight.add(label);
    // The factory is called synchronously, as React.lazy itself would; a
    // synchronous throw is folded into the same rejection path.
    let started;
    try { started = Promise.resolve(factory()); } catch (e) { started = Promise.reject(e); }
    const load = started
      .catch((err) => {
        const original = (err && err.message) || String(err);
        if (!isStaleChunkMessage(original)) throw err;   // a code error, not a stale chunk
        // Re-shape into the wording the recovery chain keys on, keeping the
        // original text (and its URL, when the browser gave one).
        const wrapped = new Error(`Loading chunk ${label} failed. (${original})`);
        wrapped.cause = err;
        throw wrapped;
      });
    return Promise.race([
      load,
      new Promise((_, reject) => {
        timer = setTimeout(() => {
          // Message intentionally matches the old webpack chunk-error wording so
          // the existing recovery/logging predicates recognise it.
          reject(new Error(`Loading chunk ${label} failed. (timeout after ${chunkDeadlineMs}ms)`));
        }, chunkDeadlineMs);
      }),
    ]).finally(() => {
      clearTimeout(timer);
      inFlight.delete(label);
    });
  });
}
